import React, { useState, useEffect, useRef } from 'react';
import { Upload, FileEdit, Save, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DocumentParserService } from './documentParser';
import {
    Box,
    Button,
    TextField,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Alert,
    CircularProgress,
    Grid,
} from '@mui/material';
import {
    useGenerateJobDescriptionMutation,
    useUpdateJobDescriptionMutation,
    useGetPreHiringJDMutation,
} from '../../../../../api/preHQ';

// Quill Modules configuration
const modules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link'],
        ['clean'],
    ],
};

const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'list',
    'bullet',
    'align',
    'link',
];

// Utility to sanitize HTML: remove <style> tags and inline font-family styles
const sanitizeHtml = (html) => {
    // Remove <style>...</style> tags
    html = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    // Remove inline font-family styles
    html = html.replace(/font-family:[^;"']*;?/gi, '');
    return html;
};

const JobDescription = ({ form }) => {
    const {
        formState: { errors },
    } = form;
    const [isEditing, setIsEditing] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [hasContent, setHasContent] = useState(false);
    const [editorContent, setEditorContent] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiTemplates, setAITemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(null);
    const [selectedTemplateType, setSelectedTemplateType] = useState(null); // 'ai' or 'predefined'
    const [uploadedFile, setUploadedFile] = useState(null);
    const fileInputRef = useRef(null);
    const editorRef = useRef(null);
    // Add a new state to track if a file is uploaded or a template is selected
    const [isFileOrTemplateSelected, setIsFileOrTemplateSelected] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [hoveredIndex, setHoveredIndex] = useState(-1);
    const searchInputRef = useRef(null);
    const [searchBoxWidth, setSearchBoxWidth] = useState(0);
    const quickPrompts = [
        'Make this job description more professional and polished for senior-level candidates',
        'Add comprehensive technical requirements and specific technology stack details',
        'Include detailed remote work policies and flexible working arrangements',
    ];
    // Toggle: show templates or editor
    // hasContent is used as the toggle
    // Handler for 'Change Template' button - COMMENTED OUT FOR NOW
    // const handleChangeTemplate = () => {
    //     setHasContent(false);
    //     setSelectedTemplate(null);
    //     setUploadedFile(null);
    //     setIsFileOrTemplateSelected(false);
    //     setEditorContent('');
    //     form.setValue('description', '', {
    //         shouldValidate: true,
    //         shouldDirty: true,
    //     });
    // };

    // Update isFileOrTemplateSelected when template or file is selected
    useEffect(() => {
        setIsFileOrTemplateSelected(!!selectedTemplate || !!uploadedFile);
    }, [selectedTemplate, uploadedFile]);

    // Use RTK Query mutation for job description generation
    const [generateJobDescription, { data: generateJobDescriptionData, isLoading: isGeneratingJobDescription }] =
        useGenerateJobDescriptionMutation();
    const [updateJobDescription, { data: updateJobDescriptionData, isLoading: isUpdatingJobDescription }] =
        useUpdateJobDescriptionMutation();
    const [getPreHiringJD, { data: getPreHiringJDData, isLoading: getPreHiringJDLoading }] =
        useGetPreHiringJDMutation();

    // Initialize content from form
    useEffect(() => {
        const existingContent = form.getValues('description');
        if (existingContent) {
            setEditorContent(existingContent);
            setHasContent(true);
        }
    }, [form]);

    // Helper to get aitemplates from form with correct type
    const getAITemplates = () => {
        const val = form.getValues('aitemplates');
        return Array.isArray(val) ? val : [];
    };

    // Initialize AI templates
    useEffect(() => {
        const formAITemplates = getAITemplates();
        if (formAITemplates.length > 0) {
            setAITemplates(formAITemplates);
        } else if (aiTemplates.length === 0) {
            generateJobDescription({
                job_title: form.getValues('jobTitle'),
                job_category: form.getValues('department'),
                job_skills: form.getValues('mandatorySkills'),
            });
        }
        getPreHiringJD();
    }, []);

    // Process AI generation data when it changes
    useEffect(() => {
        if (generateJobDescriptionData) {
            const extractedTemplates = Object.values(generateJobDescriptionData?.data).filter(
                (item) => typeof item === 'string',
            );
            setAITemplates(extractedTemplates);
            form.setValue('aitemplates', extractedTemplates);

            // Auto-load first template into editor
            if (extractedTemplates.length > 0) {
                const firstTemplate = extractedTemplates[0];
                setEditorContent(firstTemplate);
                setHasContent(true);
                setSelectedTemplate(firstTemplate);
                setSelectedTemplateIndex(0);
                setSelectedTemplateType('ai');
                setIsFileOrTemplateSelected(true);
                form.setValue('description', firstTemplate, {
                    shouldValidate: true,
                    shouldDirty: true,
                });
            }
        }
    }, [generateJobDescriptionData, form]);
    // Helper to extract <title> from HTML string
    const extractTitle = (html, fallback) => {
        const match = html.match(/<title>(.*?)<\/title>/i);
        return match ? match[1] : fallback;
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.match(/\.(pdf|docx)$/i)) {
            toast.error('Please upload a PDF or DOCX file');
            return;
        }

        setIsLoading(true);
        try {
            // const parser = new DocumentParserService();
            const parsedContent = await DocumentParserService.parseDocument(file);
            setEditorContent(parsedContent);
            setHasContent(true);
            setUploadedFile(file);
            setIsFileOrTemplateSelected(true);
            form.setValue('description', parsedContent, {
                shouldValidate: true,
                shouldDirty: true,
            });
            setSelectedTemplateType(null);
            toast.success('File uploaded successfully. Your document has been parsed and loaded.');
        } catch (error) {
            console.error('Error parsing file:', error);
            toast.error('Error parsing file. Please try again.');
        } finally {
            setIsLoading(false);
            // Reset the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleEditorChange = (content) => {
        setEditorContent(content);
        const trimmedContent = content.replace(/<(.|\n)*?>/g, '').trim();
        setHasContent(!!trimmedContent);
        form.setValue('description', content, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    const handleSave = () => {
        form.setValue('description', editorContent, {
            shouldValidate: true,
            shouldDirty: true,
        });
        setIsEditing(false);
        toast.success('Changes saved. Your job description has been updated.');
    };

    const handleTemplateSelect = (template, isPreHiringJD = false, index = null) => {
        let content = '';
        if (isPreHiringJD) {
            content = `<b> ${template?.name} - ${template?.description}</b><br/>${template?.content}`;
            setEditorContent(content);
            setSelectedTemplateIndex(index);
            setSelectedTemplateType('predefined');
        } else {
            content = template;
            setEditorContent(content);
            setSelectedTemplateIndex(index);
            setSelectedTemplateType('ai');
        }
        setHasContent(true);
        setSelectedTemplate(content);
        setIsFileOrTemplateSelected(true);
        form.setValue('description', content, {
            shouldValidate: true,
            shouldDirty: true,
        });
        setIsEditing(true);
        setAiPrompt('');
    };

    // Update editor and form when updateJobDescriptionData changes
    useEffect(() => {
        if (updateJobDescriptionData?.data?.updated_job_description_html) {
            setEditorContent(updateJobDescriptionData.data.updated_job_description_html);
            setHasContent(true);
            form.setValue('description', updateJobDescriptionData.data.updated_job_description_html, {
                shouldValidate: true,
                shouldDirty: true,
            });
            toast.success('Job description updated successfully!');
            setTimeout(() => {
                editorRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
            setAiPrompt('');
        }
    }, [updateJobDescriptionData, form]);

    const handleAiGenerate = async (customPrompt = null) => {
        const promptToUse = customPrompt || aiPrompt;
        if (!promptToUse.trim()) {
            toast.error('Please enter a prompt to generate content.');
            return;
        }
        try {
            // Ensure <html> tags are present
            let htmlWrappedContent = editorContent;
            if (!/^\s*<html[ >]/i.test(editorContent)) {
                htmlWrappedContent = `<html>${editorContent}</html>`;
            }
            await updateJobDescription({
                job_description: htmlWrappedContent,
                user_prompt: promptToUse,
            });
            setAiPrompt('');
        } catch (error) {
            toast.error("Couldn't update job description");
        }
    };

    // Update search results for dropdown
    useEffect(() => {
        if (getPreHiringJDData?.jobDescriptions) {
            const filtered = getPreHiringJDData.jobDescriptions.filter((jd) =>
                jd.name.toLowerCase().includes(searchText.toLowerCase()),
            );
            setSearchResults(filtered);
        }
    }, [searchText, getPreHiringJDData]);

    // Handle dropdown close on blur
    const handleSearchBlur = (e) => {
        // Timeout to allow click event to register before closing
        setTimeout(() => setSearchDropdownOpen(false), 100);
    };

    // Handle keyboard navigation
    const handleSearchKeyDown = (e) => {
        if (!searchDropdownOpen || searchResults.length === 0) return;
        if (e.key === 'ArrowDown') {
            setHoveredIndex((prev) => (prev + 1) % searchResults.length);
        } else if (e.key === 'ArrowUp') {
            setHoveredIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
        } else if (e.key === 'Enter' && hoveredIndex >= 0) {
            handleTemplateSelect(searchResults[hoveredIndex], true);
            setSearchDropdownOpen(false);
            setSearchText('');
        }
    };

    // Handle dropdown item click
    const handleDropdownSelect = (template, index) => {
        handleTemplateSelect(template, true, index);
        setSearchDropdownOpen(false);
        setSearchText('');
    };

    // Update searchbox width for dropdown alignment
    useEffect(() => {
        if (searchInputRef.current) {
            setSearchBoxWidth(searchInputRef.current.offsetWidth);
        }
    }, [searchDropdownOpen]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: '100vh' }}>
            <Grid container spacing={{ xs: 2, md: 3 }}>
                {/* Left Column - Templates */}
                <Grid item xs={12} md={3} sx={{ order: { xs: 2, md: 1 } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {/* Search Template Section - Fixed */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Typography
                                    variant="subtitle1"
                                    component="label"
                                    sx={{ fontWeight: 500, whiteSpace: 'nowrap', fontSize: '0.875rem' }}
                                >
                                    Search Template
                                </Typography>
                                <Box sx={{ position: 'relative', flex: 1, maxWidth: 200 }}>
                                    <TextField
                                        inputRef={searchInputRef}
                                        placeholder="Search..."
                                        size="small"
                                        fullWidth
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: 32,
                                            },
                                        }}
                                        value={searchText}
                                        onChange={(e) => {
                                            setSearchText(e.target.value);
                                            setSearchDropdownOpen(true);
                                            setHoveredIndex(-1);
                                        }}
                                        onFocus={() => {
                                            setSearchDropdownOpen(true);
                                        }}
                                        onBlur={handleSearchBlur}
                                        onKeyDown={handleSearchKeyDown}
                                    />
                                    {/* Dropdown for search results */}
                                    {searchDropdownOpen && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                width: '100%',
                                                bgcolor: 'background.paper',
                                                boxShadow: 3,
                                                borderRadius: 2,
                                                zIndex: 10,
                                                mt: 1,
                                                maxHeight: 240,
                                                overflowY: 'auto',
                                            }}
                                        >
                                            {searchResults.length > 0 ? (
                                                searchResults.map((template, idx) => (
                                                    <Box
                                                        key={template.id || idx}
                                                        sx={{
                                                            px: 2,
                                                            py: 1.5,
                                                            cursor: 'pointer',
                                                            bgcolor:
                                                                hoveredIndex === idx ? 'grey.100' : 'background.paper',
                                                            color: 'text.primary',
                                                            fontWeight: 500,
                                                            fontSize: '0.875rem',
                                                            borderBottom: '1px solid #f0f0f0',
                                                            '&:last-child': { borderBottom: 'none' },
                                                        }}
                                                        onMouseEnter={() => setHoveredIndex(idx)}
                                                        onMouseLeave={() => setHoveredIndex(-1)}
                                                        onMouseDown={() => handleDropdownSelect(template, idx)}
                                                    >
                                                        {template.name}
                                                    </Box>
                                                ))
                                            ) : (
                                                <Box
                                                    sx={{
                                                        px: 2,
                                                        py: 1.5,
                                                        color: 'text.secondary',
                                                        fontSize: '0.875rem',
                                                    }}
                                                >
                                                    No results found
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Templates Section - Scrollable */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                minHeight: { xs: 'auto', md: '440px' },
                                maxHeight: { xs: 'none', md: '61vh' },
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': {
                                    display: 'none',
                                },
                                scrollbarWidth: 'none', // Firefox
                                msOverflowStyle: 'none', // IE and Edge
                            }}
                        >
                            {/* AI Generated Templates Section */}
                            <Box>
                                <Typography variant="h6" component="h3" sx={{ fontWeight: 500, mb: 2 }}>
                                    AI Generated Templates
                                </Typography>
                                {isGeneratingJobDescription ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {[1, 2, 3].map((index) => (
                                            <Card key={index} sx={{ p: 1.5 }}>
                                                <Box
                                                    sx={{
                                                        height: 20,
                                                        bgcolor: 'grey.200',
                                                        borderRadius: 1,
                                                        width: '60%',
                                                        mb: 1,
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        height: 14,
                                                        bgcolor: 'grey.200',
                                                        borderRadius: 1,
                                                        width: '100%',
                                                    }}
                                                />
                                            </Card>
                                        ))}
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'visible' }}
                                    >
                                        {aiTemplates.length > 0 ? (
                                            aiTemplates.slice(0, 3).map((template, index) => {
                                                const titleMatch = template.match(/<title>(.*?)<\/title>/i);
                                                const title = titleMatch ? titleMatch[1] : `Template ${index + 1}`;
                                                const pMatch = template.match(/<p[^>]*>(.*?)<\/p>/is);
                                                const firstLine = pMatch
                                                    ? pMatch[1].replace(/<[^>]+>/g, '').trim()
                                                    : '';
                                                return (
                                                    <Card
                                                        key={index}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            p: 1.5,
                                                            boxShadow: 1,
                                                            border: 2,
                                                            borderColor:
                                                                selectedTemplateIndex === index &&
                                                                selectedTemplateType === 'ai'
                                                                    ? '#ED7342'
                                                                    : 'grey.200',
                                                            transition: 'box-shadow 0.2s, border-color 0.2s',
                                                            position: 'relative',
                                                            overflow: 'visible',
                                                            '&:hover': {
                                                                borderColor: '#ED7342',
                                                                boxShadow: 2,
                                                            },
                                                        }}
                                                        onClick={() => handleTemplateSelect(template, false, index)}
                                                    >
                                                        {selectedTemplateIndex === index &&
                                                            selectedTemplateType === 'ai' && (
                                                                <Box
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: -6,
                                                                        right: 4,
                                                                        bgcolor: '#ED7342',
                                                                        color: 'white',
                                                                        px: 0.75,
                                                                        py: 0.25,
                                                                        borderRadius: 0.5,
                                                                        fontSize: '0.625rem',
                                                                        fontWeight: 600,
                                                                        zIndex: 999,
                                                                        boxShadow: 1,
                                                                    }}
                                                                >
                                                                    Recommended
                                                                </Box>
                                                            )}
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                color: '#ED7342',
                                                                fontWeight: 600,
                                                                fontSize: '0.875rem',
                                                                mb: 0.5,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {title}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                fontSize: '0.75rem',
                                                                lineHeight: 1.2,
                                                            }}
                                                        >
                                                            {firstLine}
                                                        </Typography>
                                                    </Card>
                                                );
                                            })
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    py: 3,
                                                    color: 'text.secondary',
                                                    fontSize: '0.875rem',
                                                }}
                                            >
                                                Templates not available at the moment
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Predefined Job Descriptions Section */}
                            {getPreHiringJDData?.jobDescriptions?.length > 0 || getPreHiringJDLoading ? (
                                <Box>
                                    <Typography variant="h6" component="h3" sx={{ fontWeight: 500, mb: 2 }}>
                                        Predefined Job Descriptions
                                    </Typography>
                                    {getPreHiringJDLoading ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {[1, 2, 3].map((index) => (
                                                <Card key={index} sx={{ p: 1.5 }}>
                                                    <Box
                                                        sx={{
                                                            height: 20,
                                                            bgcolor: 'grey.200',
                                                            borderRadius: 1,
                                                            width: '60%',
                                                            mb: 1,
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            height: 14,
                                                            bgcolor: 'grey.200',
                                                            borderRadius: 1,
                                                            width: '100%',
                                                        }}
                                                    />
                                                </Card>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1.5,
                                                overflow: 'visible',
                                            }}
                                        >
                                            {getPreHiringJDData?.jobDescriptions.slice(0, 3).map((template, index) => (
                                                <Card
                                                    key={index}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        p: 1.5,
                                                        boxShadow: 1,
                                                        border: 2,
                                                        borderColor:
                                                            selectedTemplateIndex === index &&
                                                            selectedTemplateType === 'predefined'
                                                                ? '#ED7342'
                                                                : 'grey.200',
                                                        transition: 'box-shadow 0.2s, border-color 0.2s',
                                                        position: 'relative',
                                                        overflow: 'visible',
                                                        '&:hover': {
                                                            borderColor: '#ED7342',
                                                            boxShadow: 2,
                                                        },
                                                    }}
                                                    onClick={() => handleTemplateSelect(template, true, index)}
                                                >
                                                    {selectedTemplateIndex === index &&
                                                        selectedTemplateType === 'predefined' && (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: -6,
                                                                    right: 4,
                                                                    bgcolor: '#ED7342',
                                                                    color: 'white',
                                                                    px: 0.75,
                                                                    py: 0.25,
                                                                    borderRadius: 0.5,
                                                                    fontSize: '0.625rem',
                                                                    fontWeight: 600,
                                                                    zIndex: 1,
                                                                    boxShadow: 1,
                                                                }}
                                                            >
                                                                Recommended
                                                            </Box>
                                                        )}
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            color: '#ED7342',
                                                            fontWeight: 600,
                                                            fontSize: '0.875rem',
                                                            mb: 0.5,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {template?.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            fontSize: '0.75rem',
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {template?.description}
                                                    </Typography>
                                                </Card>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                </Grid>

                {/* Right Column - Editor */}
                <Grid item xs={12} md={9} sx={{ order: { xs: 1, md: 2 } }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                            minHeight: { xs: 'auto', md: '500px' },
                            maxHeight: { xs: 'none', md: '70vh' },
                            overflowY: 'auto',
                        }}
                    >
                        {/* Upload Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={isLoading ? <CircularProgress size={16} /> : <Upload />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isLoading}
                                sx={{
                                    color: '#ED7342',
                                    borderColor: '#ED7342',
                                    '&:hover': {
                                        backgroundColor: '#FFF4F0',
                                        borderColor: '#ED7342',
                                    },
                                }}
                            >
                                Upload Document
                            </Button>
                        </Box>

                        {/* Editor Section */}
                        <Card sx={{ p: 3, boxShadow: 'none', border: 'none' }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="h6" component="h3" sx={{ fontWeight: 500, mb: 2, mt: -3 }}>
                                    Job Description Editor
                                </Typography>
                                {errors.description && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {errors.description.message}
                                    </Alert>
                                )}
                            </Box>
                            {isGeneratingJobDescription ? (
                                <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 3 }}>
                                    {/* Editor Skeleton */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {/* Title skeleton */}
                                        <Box
                                            sx={{
                                                height: 24,
                                                bgcolor: 'grey.200',
                                                borderRadius: 1,
                                                width: '60%',
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                            }}
                                        />
                                        {/* Content lines skeleton */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                                            <Box
                                                sx={{
                                                    height: 16,
                                                    bgcolor: 'grey.200',
                                                    borderRadius: 1,
                                                    width: '100%',
                                                    animation: 'pulse 1.5s ease-in-out infinite',
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    height: 16,
                                                    bgcolor: 'grey.200',
                                                    borderRadius: 1,
                                                    width: '95%',
                                                    animation: 'pulse 1.5s ease-in-out infinite',
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    height: 16,
                                                    bgcolor: 'grey.200',
                                                    borderRadius: 1,
                                                    width: '85%',
                                                    animation: 'pulse 1.5s ease-in-out infinite',
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            ) : isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                                    <CircularProgress size={32} sx={{ color: '#ED7342' }} />
                                </Box>
                            ) : (
                                <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                                    <Box ref={editorRef}>
                                        <ReactQuill
                                            theme="snow"
                                            value={editorContent}
                                            onChange={handleEditorChange}
                                            modules={modules}
                                            formats={formats}
                                            readOnly={!isEditing}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Card>
                    </Box>
                </Grid>
            </Grid>

            {/* Enhance with AI Section - Below the grid */}
            <Card sx={{ p: 3, mt: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Sparkles style={{ color: '#ED7342', width: 16, height: 16 }} />
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 500 }}>
                        Enhance with AI
                    </Typography>
                </Box>

                {/* Quick Prompts */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {quickPrompts.map((prompt, index) => (
                            <Box
                                key={index}
                                component="button"
                                onClick={() => {
                                    setAiPrompt(prompt);
                                    // Trigger API call immediately with the prompt
                                    setTimeout(() => {
                                        handleAiGenerate(prompt);
                                    }, 100);
                                }}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    bgcolor: '#FFF4F0',
                                    color: '#ED7342',
                                    border: '1px solid #ED7342',
                                    borderRadius: '20px',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    textAlign: 'left',
                                    width: 'fit-content',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&:hover': {
                                        bgcolor: '#ED7342',
                                        color: 'white',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 2px 4px rgba(237, 115, 66, 0.2)',
                                    },
                                }}
                            >
                                {prompt}
                                <Box sx={{ fontSize: '1rem', fontWeight: 600 }}>+</Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        placeholder="Add Prompt to update the job description..."
                        multiline
                        rows={4}
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        fullWidth
                        variant="outlined"
                    />
                    <Button
                        variant="contained"
                        startIcon={isUpdatingJobDescription ? <CircularProgress size={16} /> : <Sparkles />}
                        onClick={handleAiGenerate}
                        disabled={isUpdatingJobDescription}
                        sx={{
                            bgcolor: '#ED7342',
                            '&:hover': {
                                bgcolor: '#d26539',
                            },
                            alignSelf: 'flex-end',
                        }}
                    >
                        Update Description
                    </Button>
                </Box>
                {errors.description && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {errors.description.message}
                    </Alert>
                )}
            </Card>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
            />

            <style>{`
                @keyframes pulse {
                    0% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.5;
                    }
                    100% {
                        opacity: 1;
                    }
                }
                .quill {
                    height: 400px;
                }
                .ql-container {
                    font-size: 16px;
                    height: calc(400px - 42px);
                    overflow-y: auto;
                    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                }
                .ql-toolbar {
                    border-top-left-radius: 6px;
                    border-top-right-radius: 6px;
                    background-color: #f8f9fa;
                    border-color: #e2e8f0;
                }
                .ql-container {
                    border-bottom-left-radius: 6px;
                    border-bottom-right-radius: 6px;
                    border-color: #e2e8f0;
                    border-bottom: 1px solid #e2e8f0;
                }
                .quill-view-only .ql-toolbar {
                    display: none;
                }
                .quill-view-only .ql-container {
                    border: none;
                }
                .ql-editor {
                    padding: 1.5rem;
                    line-height: 1.8;
                    letter-spacing: 0.01em;
                }
                .ql-editor p {
                    margin-bottom: 1.25rem;
                }
                .ql-editor h1, .ql-editor h2, .ql-editor h3 {
                    margin-top: 2rem;
                    margin-bottom: 1.25rem;
                    font-weight: 600;
                    line-height: 1.3;
                }
                .ql-editor h1 {
                    font-size: 1.875rem;
                    color: #111827;
                }
                .ql-editor h2 {
                    font-size: 1.5rem;
                    color: #1f2937;
                }
                .ql-editor h3 {
                    font-size: 1.25rem;
                    color: #374151;
                }
                .ql-editor ul, .ql-editor ol {
                    margin-bottom: 1.25rem;
                    padding-left: 2rem;
                }
                .ql-editor li {
                    margin-bottom: 0.75rem;
                    padding-left: 0.5rem;
                }
                .ql-editor li::before {
                    display: inline-block;
                    white-space: nowrap;
                    width: 1.2em;
                }
                .ql-editor strong {
                    font-weight: 600;
                    color: #111827;
                }
                .ql-editor a {
                    color: #2563eb;
                    text-decoration: underline;
                }
                .ql-editor a:hover {
                    color: #1d4ed8;
                }
                .ql-editor blockquote {
                    border-left: 4px solid #e5e7eb;
                    padding-left: 1rem;
                    margin-left: 0;
                    margin-right: 0;
                    font-style: italic;
                    color: #4b5563;
                }
                .ql-snow .ql-toolbar button:hover,
                .ql-snow .ql-toolbar button:focus,
                .ql-snow .ql-toolbar button.ql-active {
                    color: #ED7342;
                }
                .ql-snow .ql-toolbar button:hover .ql-stroke,
                .ql-snow .ql-toolbar button:focus .ql-stroke,
                .ql-snow .ql-toolbar button.ql-active .ql-stroke {
                    stroke: #ED7342;
                }
                .ql-snow .ql-toolbar button:hover .ql-fill,
                .ql-snow .ql-toolbar button:focus .ql-fill,
                .ql-snow .ql-toolbar button.ql-active .ql-fill {
                    fill: #ED7342;
                }
                .preview-html, .preview-html *, .preview-html pre, .preview-html code {
                    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
                }
            `}</style>
        </Box>
    );
};

export default JobDescription;
